/* eslint-disable no-use-before-define */
import uniqBy from 'lodash/uniqBy';
import groupBy from 'lodash/groupBy';
import { Map } from 'immutable';

import {
  drawing as drawingSelector,
  selectedOptionsNameValueObjectsArray as selectedOptionsNameValueObjectsArraySelector,
} from '@Containers/ResultsFilters/ResultsFiltersComponents/StructureFilter/StructureFilterSelectors';

import { byValue, ascending, descending, occurrence } from '@Containers/ResultsFilters/ResultsFiltersConstants';
import { convertSelectedItemsFromDialogToProperties } from '@Containers/ResultsFilters/ResultsFiltersComponents/SelectedItemsFromDialog/SelectedItemsFromDialogActions';
import { embedFacetsObj } from './facetsForTrackingUtils';

export function getSortingParams(sortingType, sorting) {
  const base = { override: true }; // override means not default one, requires params

  if (sortingType === byValue) {
    // 'byValue' should change 'ascending', 'ascending' should change only itself (comments of RXFE-1018)

    return {
      byValue: true,
      ascending: sorting === ascending,
      ...base,
    };
  }

  return {
    ascending: sorting === ascending,
    ...base,
  };
}

// eslint-disable-next-line consistent-return
export function getHandlersByFilterType(filter) {
  const userInputOpen = filter.get('userInputOpen');
  const userInputValue = filter.get('userInputValue');
  const fullText = filter.get('fullText');
  const type = filter.get('type');
  const groups = filter.get('groups');
  const hasNonEmptyUserInput = typeof userInputValue === 'string' && userInputValue.trim() !== '';

  if (fullText === true && hasNonEmptyUserInput) {
    return handlerFilterFullText;
  }

  if (userInputOpen && userInputValue) {
    return handlerFilterUserInput;
  }

  if (type === 5) {
    return handlerStructureFilter;
  }

  if (type === 4) {
    return handlerCheckboxFilter;
  }

  if (groups && groups.count()) {
    return handlerFilterWithGroups;
  }
}

export function handlerFilterFullText(filter) {
  const userInputValue = filter.get('userInputValue');
  const id = filter.get('id');

  return {
    title: filter.get('title'),
    property: id,
    userInput: userInputValue,
    ...embedFacetsObj({
      name: filter.get('title'),
      values: [userInputValue],
    }),
  };
}

export function handlerFilterUserInput(filter) {
  const userInputValue = filter.get('userInputValue');
  const id = filter.get('id');

  return {
    property: id,
    userInput: userInputValue,
    ...embedFacetsObj({
      name: filter.get('title'),
      values: [userInputValue],
      isFilterUserValue: true,
    }),
  };
}

export function handlerFilterWithGroups(filter) {
  if (filter.get('selectedItemsFromDialog')) {
    return convertSelectedItemsFromDialogToProperties(filter, filter.get('selectedItemsFromDialog'));
  }
  const onlySelectedGroups = filter.get('groups').filter(onlySelected);

  if (onlySelectedGroups.count() === 0) {
    return null;
  }

  return processWithOnlySelectedGroups(filter, onlySelectedGroups).map(({ property }) => property);
}

function chunksToList(chunks) {
  return chunks?.reduce((res, item) => res.concat(item.get('items')), Map()).toList();
}

function onlySelected(item) {
  return item.get('selected');
}

export function handleFilterWithChunks(filter, sortingType) {
  const batch1 = chunksToList(filter.getIn(['chunks', occurrence, ascending], Map())).filter(onlySelected);
  const batch2 = chunksToList(filter.getIn(['chunks', occurrence, descending], Map())).filter(onlySelected);
  const batch3 = chunksToList(filter.getIn(['chunks', byValue, ascending], Map())).filter(onlySelected);
  const batch4 = chunksToList(filter.getIn(['chunks', byValue, descending], Map())).filter(onlySelected);
  const batch5 = filter.get('groups').filter(onlySelected);

  if (!sortingType) {
    const result = processWithOnlySelectedGroups(filter, batch1, getSortingParams(occurrence, ascending))
      .concat(processWithOnlySelectedGroups(filter, batch2, getSortingParams(occurrence, descending)))
      .concat(processWithOnlySelectedGroups(filter, batch3, getSortingParams(byValue, ascending)))
      .concat(processWithOnlySelectedGroups(filter, batch4, getSortingParams(byValue, descending)))
      .concat(processWithOnlySelectedGroups(filter, batch5));

    const uniqResult = uniqBy(result, ({ property }) => property.title);
    return {
      properties: uniqResult.map(({ property }) => property),
      selectedItems: uniqResult.map(({ selectedItems }) => selectedItems),
    };
  }

  if (sortingType === byValue) {
    const result = processWithOnlySelectedGroups(filter, batch3, getSortingParams(byValue, ascending)).concat(
      processWithOnlySelectedGroups(filter, batch4, getSortingParams(byValue, descending))
    );

    const uniqResult = uniqBy(result, ({ property }) => property.title);
    return {
      properties: uniqResult.map(({ property }) => property),
      selectedItems: uniqResult.map(({ selectedItems }) => selectedItems),
    };
  }

  const result = processWithOnlySelectedGroups(filter, batch1, getSortingParams(occurrence, ascending))
    .concat(processWithOnlySelectedGroups(filter, batch2, getSortingParams(occurrence, descending)))
    .concat(processWithOnlySelectedGroups(filter, batch5));

  const uniqResult = uniqBy(result, ({ property }) => property.title);
  return {
    properties: uniqResult.map(({ property }) => property),
    selectedItems: uniqResult.map(({ selectedItems }) => selectedItems),
  };
}

function handleRootThesaurusItem(filter) {
  const property = filter.get('id');
  const items = filter.getIn(['thesaurusGroups', 'items']);
  const root = filter.get('thesaurusRootId');
  const rootedItems = items.filter((item) => item.get('rootIds').last() === root);

  return [
    {
      property,
      root,
      indexes: rootedItems
        .map((item) => item.get('index'))
        .valueSeq()
        .toArray(),
      ...embedFacetsObj({
        name: filter.get('title'),
        values: rootedItems
          .map((item) => item.get('title'))
          .valueSeq()
          .toArray(),
      }),
    },
  ];
}

export function handleThesaurusItems(filter, tags) {
  const isAllSelected = filter.get('selected');

  if (isAllSelected) {
    return handleRootThesaurusItem(filter);
  }

  const property = filter.get('id');
  const groupedTagsByLastRootId = tags.reduce((res, item) => {
    const root = item.get('rootIds').last();
    if (!res[root]) {
      res[root] = [];
    }

    res[root].push(item);
    return res;
  }, {});

  return Object.keys(groupedTagsByLastRootId).map((root) => {
    return {
      property,
      root,
      indexes: groupedTagsByLastRootId[root].map((item) => {
        return item.get('index');
      }),
      ...embedFacetsObj({
        name: filter.get('title'),
        values: groupedTagsByLastRootId[root].map((item) => item.get('title')),
      }),
    };
  });
}

export function processWithOnlySelectedGroups(filter, onlySelectedGroups, sortingExtention = {}) {
  const filterId = filter.get('id');
  const title = filter.get('title');
  const groupedBySorting = groupBy(onlySelectedGroups.toArray(), (item) => {
    const sort = item.get('sort');
    if (!sort) {
      return 'default';
    }
    return JSON.stringify(sort);
  });
  const result = [];
  Object.keys(groupedBySorting).forEach((key) => {
    const group = groupedBySorting[key];
    const sort = group[0].get('sort');
    const sortPropertyIfNeeded = sort ? { sort } : {};

    const preparePropertyObject = (indexes, property, values, searchBy) => {
      const object = {
        title,
        property,
        indexes,
        ...sortingExtention,
        ...sortPropertyIfNeeded,
        ...embedFacetsObj({
          name: title,
          values,
        }),
      };
      if (searchBy) {
        object.searchBy = searchBy;
      }
      return object;
    };

    const prepareGeneralResult = () => {
      const filteredGroup = group.filter((item) => {
        return !item.get('searchBy');
      });

      if (filteredGroup.length === 0) {
        return;
      }
      const indexes = filteredGroup.map((item) => {
        return item.get('index');
      });

      const groupResult = preparePropertyObject(
        indexes,
        filterId,
        filteredGroup.map((item) => item.get('title'))
      );
      const selectedItems = filteredGroup.map((item) => {
        return {
          id: item.get('id'),
          index: item.get('index'),
          sortingType: sortingExtention.byValue ? byValue : occurrence,
          sorting: sortingExtention.ascending ? ascending : descending,
        };
      });

      result.push({
        property: groupResult,
        selectedItems,
      });
    };
    // RX-5586 handle custom fields in response for "Availability in other databases" filter
    const searchByGroups = group.filter((item) => {
      return item.get('searchBy');
    });

    const prepareSearchByGroupResult = () =>
      searchByGroups.forEach((groupItem) => {
        const searchBy = groupItem.get('searchBy');
        const index = groupItem.get('index');
        const id = groupItem.get('id');
        // eslint-disable-next-line no-shadow
        const title = groupItem.get('title');
        const existedItemIndex = result.findIndex(({ property: propertyItem }) => propertyItem.searchBy === searchBy);

        if (existedItemIndex !== -1) {
          result[existedItemIndex].property.indexes.push(index);
          result[existedItemIndex].selectedItems.push({
            id,
            index,
            sortingType: sortingExtention.byValue ? byValue : occurrence,
            sorting: sortingExtention.ascending ? ascending : descending,
          });
          result[existedItemIndex].property._facetsForTracking.values.push(title);
          return;
        }

        const groupResult = preparePropertyObject([index], filterId, [title], searchBy);
        const selectedItems = [
          {
            id,
            index,
            sortingType: sortingExtention.byValue ? byValue : occurrence,
            sorting: sortingExtention.ascending ? ascending : descending,
          },
        ];

        result.push({ property: groupResult, selectedItems });
      });

    prepareGeneralResult();

    const isSearchByGroups = searchByGroups.length > 0;

    if (isSearchByGroups) {
      prepareSearchByGroupResult();
    }
  });
  return result;
}

export function handlerCheckboxFilter(filter) {
  const id = filter.get('id');
  const selected = filter.get('selected');

  if (!selected) {
    return null;
  }

  return {
    property: id,
    ...embedFacetsObj({
      name: filter.get('title'),
      values: [`selected-${new Date().valueOf()}`],
    }),
  };
}

export function handlerStructureFilter(filter, state) {
  const drawing = drawingSelector(state);
  const id = filter.get('id');
  const selected = filter.get('selected');

  if (!drawing || !selected) {
    return null;
  }

  const options = selectedOptionsNameValueObjectsArraySelector(state);

  return {
    property: id,
    structure: drawing.structure,
    options,
  };
}

export function preparePropertiesForRequest(filters, state) {
  return filters.reduce((res, item) => {
    const handler = getHandlersByFilterType(item);

    if (!handler) {
      return res;
    }

    const property = handler(item, state);

    if (Array.isArray(property)) {
      // eslint-disable-next-line no-param-reassign
      res = res.concat(property);
    } else if (property) {
      res.push(property);
    }

    return res;
  }, []);
}

export function preparePropertiesForStoreInBreadCrumb(propertiesForRequest, filters, state) {
  return propertiesForRequest.map((item) => {
    const { property, structure } = item;
    // eslint-disable-next-line no-shadow
    const filter = filters.find((filter) => {
      return filter.get('id') === property;
    });
    const title = filter.get('title');
    const extentions = structure ? { structurePreview: drawingSelector(state).preview } : {};

    return {
      ...item,
      ...extentions,
      title,
    };
  });
}
